import { useEffect, useState } from 'react'

import { UnavailableValue } from '@/components/ymarket-pricing/UnavailableValue'
import { getYmOfferPricing } from '@/lib/api/client/ymarket-pricing'
import type { YmOfferPricingDetail, YmPriceSnapshot } from '@/lib/api/schemas/ymarket-pricing.schema'
import { formatRubleAmount } from '@/lib/market-money'
import { formatOperationTime } from '@/lib/operations/labels'
import { BESTSELLER_TIER_LABELS, DRIFT_LABELS, DRIFT_TONES, formatYmMoney, showcasePricesText } from '@/lib/ymarket-pricing'

function promoSummary(snapshot: YmPriceSnapshot): string {
  if (snapshot.promos.length === 0) return 'Нет акций'
  const joined = snapshot.promos.filter((promo) => promo.participating).map((promo) => promo.name).sort()
  return joined.length > 0 ? joined.join(', ') : 'Не участвует'
}

type SnapshotPeriod = {
  snapshot: YmPriceSnapshot
  firstCollectedAt: string
  lastCollectedAt: string
}

function sameSnapshotValues(left: YmPriceSnapshot, right: YmPriceSnapshot): boolean {
  return left.price === right.price
    && left.discountBase === right.discountBase
    && left.minimumForPromos === right.minimumForPromos
    && left.showcasePrice === right.showcasePrice
    && left.showcaseNoPayPrice === right.showcaseNoPayPrice
    && promoSummary(left) === promoSummary(right)
}

function changedPeriods(history: readonly YmPriceSnapshot[]): SnapshotPeriod[] {
  const chronological = [...history].sort((left, right) => Date.parse(left.collectedAt) - Date.parse(right.collectedAt))
  const periods: SnapshotPeriod[] = []
  for (const snapshot of chronological) {
    const previous = periods.at(-1)
    if (previous !== undefined && sameSnapshotValues(previous.snapshot, snapshot)) {
      periods[periods.length - 1] = { ...previous, snapshot, lastCollectedAt: snapshot.collectedAt }
    } else {
      periods.push({ snapshot, firstCollectedAt: snapshot.collectedAt, lastCollectedAt: snapshot.collectedAt })
    }
  }
  return periods.reverse()
}

function periodLabel(period: SnapshotPeriod): string {
  const first = formatOperationTime(period.firstCollectedAt)?.replace(', ', ' ') ?? '—'
  const last = formatOperationTime(period.lastCollectedAt)?.replace(', ', ' ') ?? '—'
  return first === last ? first : `${first} — ${last}`
}

function PriceTrend({ history }: { history: readonly YmPriceSnapshot[] }) {
  const points = history.filter((snapshot) => snapshot.price !== null)
  if (points.length < 2) return null
  const prices = points.map((snapshot) => snapshot.price ?? 0)
  const low = Math.min(...prices)
  const high = Math.max(...prices)
  if (low === high) return null
  const span = high - low
  const width = 320
  const height = 48
  const step = width / (points.length - 1)
  const path = prices.map((price, index) => `${index === 0 ? 'M' : 'L'}${(index * step).toFixed(1)},${(height - ((price - low) / span) * (height - 6) - 3).toFixed(1)}`).join(' ')
  return (
    <figure className="space-y-1">
      <svg role="img" aria-label={`Цена за ${points.length} сборов: от ${formatRubleAmount(low)} до ${formatRubleAmount(high)}`} viewBox={`0 0 ${width} ${height}`} className="h-12 w-full text-gold-deep" preserveAspectRatio="none">
        <path d={path} fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" />
      </svg>
      <figcaption className="flex justify-between text-[11px] tab-nums text-ink-muted">
        <span>мин. {formatRubleAmount(low)}</span>
        <span>макс. {formatRubleAmount(high)}</span>
      </figcaption>
    </figure>
  )
}

function OfferFacts({ offer }: { offer: YmOfferPricingDetail }) {
  const price = offer.ym?.price ?? offer.forge.price
  const showcasePrice = offer.ym?.showcasePrice ?? null
  const nextTier = offer.bestseller === null || price === null
    ? undefined
    : [...offer.bestseller.thresholds].sort((left, right) => right.maxPrice - left.maxPrice).find((threshold) => threshold.maxPrice < price)
  return (
    <dl data-testid="market-offer-facts" className="grid gap-x-4 gap-y-1 text-[12px] sm:grid-cols-2">
      <div data-fact="showcase">
        <dt className="inline text-ink-muted">Покупатель заплатит: </dt>
        <dd className="inline tab-nums">
          {showcasePrice !== null
            ? showcasePricesText(showcasePrice, offer.ym?.showcaseNoPayPrice ?? null)
            : '—'}
        </dd>
      </div>
      {offer.bestseller !== null ? (
        <div data-fact="bestseller">
          <dt className="inline text-ink-muted">Бестселлер: </dt>
          <dd className="inline">
            {BESTSELLER_TIER_LABELS[offer.bestseller.tier]}{nextTier !== undefined ? <span className="tab-nums text-ink-muted"> · до {formatRubleAmount(nextTier.maxPrice)} — {BESTSELLER_TIER_LABELS[nextTier.tier]}</span> : null}
          </dd>
        </div>
      ) : null}
      {offer.competitorLowestPrice !== null ? (
        <div data-fact="competitor">
          <dt className="inline text-ink-muted">Конкуренты: </dt>
          <dd className="inline tab-nums">от {formatRubleAmount(offer.competitorLowestPrice)}</dd>
        </div>
      ) : null}
      <div data-fact="costs">
        <dt className="inline text-ink-muted">Комиссия Маркета: </dt>
        <dd className="inline tab-nums">{offer.costs !== null ? formatRubleAmount(offer.costs.commission) : <UnavailableValue reason={offer.costsUnavailableReason ?? 'not_collected'} />}</dd>
      </div>
    </dl>
  )
}

/** The last collected YM snapshots of one offer, read only when the owner opens them. */
export function MarketPriceHistory({ offerId }: { offerId: string }) {
  const [open, setOpen] = useState(false)
  const [detail, setDetail] = useState<YmOfferPricingDetail | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return undefined
    const controller = new AbortController()
    setDetail(null)
    setError(null)
    getYmOfferPricing(offerId, controller.signal)
      .then((value) => { if (!controller.signal.aborted) setDetail(value) })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(cause instanceof Error && cause.message !== '' ? cause.message : 'Не удалось загрузить историю цен.')
      })
    return () => controller.abort()
  }, [offerId, open])

  const history = detail === null ? [] : [...detail.history].sort((left, right) => Date.parse(right.collectedAt) - Date.parse(left.collectedAt))
  const periods = changedPeriods(history)

  return (
    <details data-testid="market-price-history" className="mt-1" onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary className="cursor-pointer select-none text-[11px] font-medium text-ink-muted hover:text-ink">История цен</summary>
      <div className="mt-2 space-y-3 rounded border border-rule px-3 py-3">
        {detail !== null ? (
          <div className="flex flex-wrap items-center gap-2 text-[12px]">
            {detail.drift.state !== 'forge_price_not_set' ? <span className={`inline-flex rounded-full px-2 py-0.5 font-semibold ${DRIFT_TONES[detail.drift.state]}`}>{DRIFT_LABELS[detail.drift.state]}</span> : null}
            {detail.quarantine.inQuarantine ? <span className="inline-flex rounded-full bg-st-unpaid/10 px-2 py-0.5 font-semibold text-st-unpaid">В карантине{detail.quarantine.verdictPrice !== null ? ` · цена Маркета ${formatRubleAmount(detail.quarantine.verdictPrice)}` : ''}</span> : null}
          </div>
        ) : null}
        {open && detail === null && error === null ? <p role="status" className="text-sm text-ink-muted">Загружаем историю…</p> : null}
        {error !== null ? <p role="alert" className="rounded-md bg-st-unpaid/10 p-3 text-sm text-st-unpaid">{error}</p> : null}
        {detail !== null ? (
          <>
            <OfferFacts offer={detail} />
            <PriceTrend history={[...history].reverse()} />
            {periods.length === 0 ? <p className="text-sm text-ink-muted">Сборов ещё не было.</p> : (
              <div className="overflow-x-auto rounded-lg border border-rule">
                <table aria-label="История цен" className="min-w-full text-left text-xs">
                  <thead className="bg-bone/60 text-ink-2">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Период</th>
                      <th className="px-3 py-2 font-semibold">Цена</th>
                      <th className="px-3 py-2 font-semibold">Зачёркнутая</th>
                      <th className="px-3 py-2 font-semibold">Мин. для акций</th>
                      <th className="px-3 py-2 font-semibold">Покупатель заплатит</th>
                      <th className="px-3 py-2 font-semibold">Акции</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periods.map((period) => {
                      const { snapshot } = period
                      return (
                        <tr key={period.firstCollectedAt} className="border-t border-rule align-top tab-nums">
                          <td className="px-3 py-2 text-ink-2">{periodLabel(period)}</td>
                          <td className="px-3 py-2 font-medium">{formatYmMoney(snapshot.price)}</td>
                          <td className={`px-3 py-2 text-ink-muted ${snapshot.discountBase === null ? 'italic' : 'line-through'}`}>{formatYmMoney(snapshot.discountBase)}</td>
                          <td className="px-3 py-2">{formatYmMoney(snapshot.minimumForPromos)}</td>
                          <td className="px-3 py-2">
                            {snapshot.showcasePrice !== null
                              ? <span title={showcasePricesText(snapshot.showcasePrice, snapshot.showcaseNoPayPrice)}>{formatRubleAmount(snapshot.showcasePrice)}</span>
                              : '—'}
                          </td>
                          <td className="px-3 py-2 text-ink-2">{promoSummary(snapshot)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
      </div>
    </details>
  )
}
